<template>
  <div class="plan-item-wrap">
    <div class="item-header">
      <div class="item-status">
        <div class="status-lbl" :class="FollowUpStatusEnum[planInfo.planStatus]">
          {{ FOLLOW_UP_STATUS_TYPE.find((s) => s.key === planInfo.planStatus)?.label ?? '' }}
        </div>
        <div v-if="planInfo.taskType === 'SF'" :style="{ color: planInfo.isCompleteSurvey ? '#0AC76F' : '#FF695B' }">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 4 8" fill="none">
            <circle cx="3" cy="3" r="3" :fill="planInfo.isCompleteSurvey ? '#0AC76F' : '#FF695B'" />
          </svg>
          {{ planInfo.isCompleteSurvey ? '已填写' : '未填写' }}
        </div>
      </div>
      <div class="item-time">
        <span>截止时间</span>
        {{ planInfo.endTime }}
      </div>
    </div>
    <div class="item-title">
      {{ planInfo.taskName }}
    </div>
    <div class="item-footer">
      <div class="footer-txt">
        <img class="item-title-img" :src="getImg(planInfo.taskType)" alt="" />
        {{ FOLLOW_UP_TASK_TYPE[planInfo.taskType] }}
      </div>
      <div class="footer-btn">
        <van-button v-if="planInfo.taskType === 'SF'" class="btn-jump" type="primary" size="mini" @click="goToQuest">
          {{ btnTxt[StatusAndIsComplete] }}
        </van-button>
        <van-icon v-else name="arrow" class="btn-icon" @click="handleJumpDetail" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FollowUpStatusEnum } from '@/enums/CommonEnum';
import { FOLLOW_UP_STATUS_TYPE, FOLLOW_UP_TASK_TYPE } from '@/constants/global';
import sfImg from '@/assets/images/follow-up-file-paste.png';
import txImg from '@/assets/images/follow-up-notification.png';
import xjImg from '@/assets/images/follow-up-user-talk.png';
import { router } from '@/router';
import { useFollowUpStore } from '@/store/modules/followUp';

const followUpStore = useFollowUpStore();

interface PlanItem {
  planInfo: {
    planId: string;
    taskName: string;
    planStatus: number;
    taskType: string;
    startTime: string;
    endTime: string;
    isCompleteSurvey: boolean;
  };
  profileId?: string;
}

const props = withDefaults(defineProps<PlanItem>(), {
  planInfo: () => {
    return {
      planId: '',
      taskName: '',
      planStatus: 2,
      taskType: '',
      startTime: '',
      endTime: '',
      isCompleteSurvey: false,
    };
  },
  profileId: '',
});

const { planInfo } = toRefs(props);

const getBtnTxt = (planStatus, isCompleteSurvey) => {
  if ((planStatus === FollowUpStatusEnum.done || planStatus === FollowUpStatusEnum.waiting) && !isCompleteSurvey) {
    return '填写';
  }
  if (planStatus === FollowUpStatusEnum.sending || (planStatus === FollowUpStatusEnum.done && isCompleteSurvey)) {
    return '查看问卷';
  }
  if (planStatus === FollowUpStatusEnum.waiting && !isCompleteSurvey) {
    return '修改问卷';
  }
};
const btnTxt = {
  write: '开始填写',
  read: '查看问卷',
  update: '修改问卷',
};
const StatusAndIsComplete = computed(() => {
  const { planStatus, isCompleteSurvey } = planInfo.value;
  if ((planStatus === FollowUpStatusEnum.done || planStatus === FollowUpStatusEnum.waiting) && !isCompleteSurvey) {
    return 'write';
  }
  if (planStatus === FollowUpStatusEnum.sending || (planStatus === FollowUpStatusEnum.done && isCompleteSurvey)) {
    return 'read';
  }
  if (planStatus === FollowUpStatusEnum.waiting && isCompleteSurvey) {
    return 'update';
  }
  return 'read';
});

const getImg = (type) => {
  switch (type) {
    case 'SF':
      return sfImg;
    case 'XJ':
      return xjImg;
    case 'TX':
      return txImg;
  }
};
const goToQuest = () => {
  router.push({
    path: '/follow-up/questionnaire',
    query: {
      planId: planInfo.value.planId,
      profileId: props.profileId,
      questStatus: StatusAndIsComplete.value,
      planStatus: planInfo.value.planStatus,
    },
  });
};
const handleJumpDetail = () => {
  router.push({
    path: '/follow-up/detail',
    query: {
      planId: planInfo.value.planId,
      profileId: props.profileId,
    },
  });
};
</script>

<style scoped lang="less">
.plan-item-wrap {
  margin-bottom: 16px;
  padding: 18px 16px 22px;
  border-radius: 4px 4px 0 0;
  background: #fff;
  text-align: left;

  .item-header {
    position: relative;

    .item-status {
      display: flex;
      height: 40px;
      line-height: 40px;
      font-style: normal;
      font-size: 24px;

      .status-lbl {
        margin-right: 24px;
        padding: 2px 8px;
        border-radius: 4px;
        width: 88px;
      }

      .status-dot {
        display: inline-block;
        border-radius: 5px;
        width: 10px;
        height: 10px;
      }
    }

    .sending {
      background: #edf0f2;
      color: #4e5761;
    }

    .waiting {
      background: #e6faf1;
      color: #0ac76f;
    }

    .done {
      background: #f4f7fa;
      color: #848e99;
    }

    .item-time {
      display: inline-block;
      position: absolute;
      right: 0;
      top: 0;
      padding: 1px;
      height: 40px;
      line-height: 40px;
      font-size: 24px;
      color: #848e99;
    }
  }

  .item-title {
    overflow: hidden;
    max-width: 480px;
    height: 60px;
    line-height: 60px;
    text-overflow: ellipsis;
    font-style: normal;
    font-size: 28px;
    color: #16181a;
    white-space: nowrap;
  }

  .item-footer {
    position: relative;

    .footer-txt {
      font-weight: 500;
      font-size: 24px;
      color: #0d7cff;

      .item-title-img {
        width: 28px;
        height: 28px;
        vertical-align: text-top;
      }
    }

    .footer-btn {
      position: absolute;
      right: 8px;
      bottom: 0;

      .btn-jump {
        padding: 8px 24px 4px;
        height: 48px;
      }

      .btn-icon {
        width: 32px;
        height: 32px;
        color: #0d7cff;
      }
    }
  }
}
</style>
